/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import {
  formatPower,
  formatOnOff,
  isValidPowerSignal,
  getSignalLedColor,
  isBitSet,
  getAlarmLedColor,
} from "../utils";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  createPowerDetectorChartConfig,
  buildPowerDetectorParams,
  buildBandIndicators,
  PowerDetectorParams,
} from "./dasChartUtils";

const MruDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Global alarms
  const mruAlarmsGlob = parsedAttributes.mruAlarmsGlob;
  const mruAlarmsMask = parsedAttributes.mruAlarmsMask;
  const hasGlobalAlarms = mruAlarmsGlob !== undefined || mruAlarmsMask !== undefined;

  // Global alarm definitions
  const globalAlarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "Over Temperature" },
    { bit: 1, name: "HW Failure" },
    { bit: 2, name: "Adjustment Fault" },
    { bit: 3, name: "Inconsistent Version" },
    { bit: 4, name: "Sum Power Overload" },
    { bit: 5, name: "DL Sum Input Power Low" },
  ];

  // Band-specific alarms (matches RIM alarm bit order)
  const bandAlarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "DL Input Power Low" },
    { bit: 1, name: "DL Power Overload" },
    { bit: 2, name: "Service Off" },
    { bit: 3, name: "Output Power" },
    { bit: 4, name: "DL Out Power High" },
    { bit: 5, name: "UL Synthesizer Unlock" },
    { bit: 6, name: "DL Synthesizer Unlock" },
    { bit: 7, name: "Reference Clock Unlock" },
    { bit: 8, name: "TDD Sync Loss" },
  ];

  // Extract band alarm data with band names
  const bandAlarms: Array<{ bandNumber: number; bandName: string; alarms: any; mask: any }> = [];
  for (let i = 1; i <= 5; i++) {
    const alarms = parsedAttributes[`mruAlarmsBand${i}`];
    const mask = parsedAttributes[`mruAlarmsMaskBand${i}`];
    const bandName = parsedAttributes[`supportedBand${i}`] || `Band ${i}`;
    if (alarms !== undefined || mask !== undefined) {
      bandAlarms.push({
        bandNumber: i,
        bandName,
        alarms,
        mask,
      });
    }
  }

  const hasBandAlarms = bandAlarms.length > 0;

  // Extract band data for bands 1-5 - MEMOIZED to prevent recreation on every render
  // Use JSON.stringify of relevant attributes as dependency to ensure stability
  const bands = useMemo(() => {
    const result: Array<{
      number: number;
      name: string;
      supported: any;
      ulLimiter: any;
      serviceState: any;
      outPower: any;
      dlPower: any;
      ulPowerDetector: any;
    }> = [];
    
    for (let i = 1; i <= 5; i++) {
      const band = {
        number: i,
        name: parsedAttributes[`supportedBand${i}`] || `Band ${i}`,
        supported: parsedAttributes[`supportedBand${i}`],
        ulLimiter: parsedAttributes[`ulLimiterBand${i}`],
        serviceState: parsedAttributes[`serviceStateBand${i}`],
        outPower: parsedAttributes[`outPowerBand${i}`],
        dlPower: parsedAttributes[`dlPowerBand${i}`],
        ulPowerDetector: parsedAttributes[`ulPowerDetectorBand${i}`],
      };
      
      // Only include if at least one attribute is present (skip 'name' check)
      if (Object.values(band).some((v, idx) => idx > 1 && v !== undefined)) {
        result.push(band);
      }
    }
    
    return result;
  }, [
    // Serialize band attributes to create stable dependency
    JSON.stringify({
      supported: [1,2,3,4,5].map(i => parsedAttributes[`supportedBand${i}`]),
      ulLimiter: [1,2,3,4,5].map(i => parsedAttributes[`ulLimiterBand${i}`]),
      serviceState: [1,2,3,4,5].map(i => parsedAttributes[`serviceStateBand${i}`]),
      outPower: [1,2,3,4,5].map(i => parsedAttributes[`outPowerBand${i}`]),
      dlPower: [1,2,3,4,5].map(i => parsedAttributes[`dlPowerBand${i}`]),
      ulPowerDetector: [1,2,3,4,5].map(i => parsedAttributes[`ulPowerDetectorBand${i}`])
    })
  ]);

  const hasBandInfo = bands.length > 0;

  // Chart fullscreen state management
  const [fullscreenChart, setFullscreenChart] = useState<'dl' | 'ul' | null>(null);

  const handleOpenFullscreen = (chartType: 'dl' | 'ul') => {
    setFullscreenChart(chartType);
  };

  const handleCloseFullscreen = () => {
    setFullscreenChart(null);
  };

  // Build chart parameters - memoized to prevent unnecessary re-renders
  const supportedBands = useMemo(() => bands.filter(b => b.supported), [bands]);
  const bandNumbers = useMemo(() => supportedBands.map(b => b.number), [supportedBands]);
  const bandNames = useMemo(() => supportedBands.map(b => b.name), [supportedBands]);

  // Extract primitive values early to create stable dependencies
  const siteId = device.servicesDeviceObjectKey || device.serialNumber || '';
  const serviceDeviceObjectKey = device.serialNumber;
  
  // RemoteEquipment object_key for chart queries
  const remoteEquipmentObjectKey = useMemo(() => {
    return device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      parsedAttributes.mruObjectId ||
      parsedAttributes.mruId ||
      'MRU-1';
  }, [
    JSON.stringify([
      device.objectKey,
      parsedAttributes.objectKey,
      parsedAttributes.manageObject,
      parsedAttributes.managedObject,
      parsedAttributes.objectId,
      parsedAttributes.mruObjectId,
      parsedAttributes.mruId
    ])
  ]);

  // Build indicators - memoized
  const ulIndicators = useMemo(() => buildBandIndicators('ulPowerDetectorBand', bandNumbers), [bandNumbers]);
  const dlIndicators = useMemo(() => buildBandIndicators('dlPowerBand', bandNumbers), [bandNumbers]);

  // Chart parameters - memoized to prevent recreation on every render
  const ulParams = useMemo<PowerDetectorParams>(() => ({
    remoteEquipmentObjectKey,
    indicators: ulIndicators,
    bandNames,
    bandNumbers,
    yAxisMin: -150,  // MRU UL min power
    yAxisMax: -20,   // MRU UL max power
  }), [remoteEquipmentObjectKey, ulIndicators, bandNames, bandNumbers]);

  const dlParams = useMemo<PowerDetectorParams>(() => ({
    remoteEquipmentObjectKey,
    indicators: dlIndicators,
    bandNames,
    bandNumbers,
    yAxisMin: -10,   // MRU DL min power
    yAxisMax: 80,    // MRU DL max power
  }), [remoteEquipmentObjectKey, dlIndicators, bandNames, bandNumbers]);

  // Memoize chart config and additional params for both UL and DL
  const ulChartConfig = useMemo(() => 
    createPowerDetectorChartConfig('ul', ulParams),
    [ulParams]
  );

  const dlChartConfig = useMemo(() => 
    createPowerDetectorChartConfig('dl', dlParams),
    [dlParams]
  );

  const ulChartAdditionalParams = useMemo(() => ({
    ...buildPowerDetectorParams(siteId, remoteEquipmentObjectKey, ulIndicators)
  }), [siteId, remoteEquipmentObjectKey, ulIndicators]);

  const dlChartAdditionalParams = useMemo(() => ({
    ...buildPowerDetectorParams(siteId, remoteEquipmentObjectKey, dlIndicators)
  }), [siteId, remoteEquipmentObjectKey, dlIndicators]);

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasGlobalAlarms && !hasBandAlarms) return null;
    
    return (
      <>
        {/* Global Alarms Section */}
        {hasGlobalAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={mruAlarmsGlob}
                alarmsMask={mruAlarmsMask}
                alarmDefinitions={globalAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}

        {/* Band Alarms Section */}
        {hasBandAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Band Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Alarm</TableHead>
                    {bandAlarms.map((band) => (
                      <TableHead key={band.bandNumber} className="text-center min-w-[120px]">
                        {band.bandName}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bandAlarmDefinitions.map((alarmDef) => (
                    <TableRow key={alarmDef.bit}>
                      <TableCell>{alarmDef.name}</TableCell>
                      {bandAlarms.map((band) => {
                        const isAlarmActive = isBitSet(band.alarms, alarmDef.bit);
                        const isMaskEnabled = isBitSet(band.mask, alarmDef.bit);
                        return (
                          <TableCell key={`${band.bandNumber}-${alarmDef.bit}`} className="text-center">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                                }}
                              />
                              <Checkbox
                                checked={isMaskEnabled}
                                disabled
                                size="small"
                                sx={{
                                  padding: 0,
                                  color: "var(--foreground)",
                                  "&.Mui-disabled": { color: "var(--muted-foreground)" },
                                }}
                              />
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show all sections except alarms
  if (!hasBandInfo) return null;

  return (
    <>
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">RF Information</CardTitle>
        </CardHeader>
        {hasBandInfo && (
        <CardContent className="p-0">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[150px]">Parameter</TableHead>
                  {bands.map((band) => (
                    <TableHead key={band.number} className="min-w-[120px]">
                      {band.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-semibold text-muted-foreground">UL Limiter</TableCell>
                  {bands.map((band) => (
                    <TableCell key={`ul-limiter-${band.number}`}>{formatOnOff(band.ulLimiter)}</TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold text-muted-foreground">Service State</TableCell>
                  {bands.map((band) => (
                    <TableCell key={`service-${band.number}`}>{formatOnOff(band.serviceState)}</TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold text-muted-foreground">Out Power</TableCell>
                  {bands.map((band) => (
                    <TableCell key={`out-power-${band.number}`}>{formatPower(band.outPower)}</TableCell>
                  ))}
                </TableRow>
                {/* Embedded DL Power Chart */}
                <TableRow className="hover:bg-transparent border-b-0">
                  <TableCell colSpan={bands.length + 1} className="px-3 pt-3 pb-0 border-b-0">
                    <div style={{ height: 300, width: '100%' }}>
                      {supportedBands.length > 0 && dlChartConfig && dlChartAdditionalParams && (
                        <DasPowerDetectorChart
                          config={dlChartConfig}
                          onFullscreenToggle={() => handleOpenFullscreen('dl')}
                          isFullscreen={false}
                          additionalParams={dlChartAdditionalParams}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent border-b-0">
                  <TableCell colSpan={bands.length + 1} className="p-3 pt-0 align-top">
                    <Card>
                      <CardContent className="p-0">
                        <div className="flex divide-x divide-border">
                          {supportedBands.map((band) => (
                            <div key={band.number} className="flex-1 px-3 py-2">
                              <p className="text-xs text-muted-foreground mb-1">{band.name}</p>
                              <div className="flex items-center gap-1.5">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(band.dlPower)) }}
                                />
                                <span className="text-sm font-semibold text-foreground">{formatPower(band.dlPower)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TableCell>
                </TableRow>
                {/* Embedded UL Power Chart */}
                <TableRow className="hover:bg-transparent border-b-0">
                  <TableCell colSpan={bands.length + 1} className="px-3 pt-3 pb-0 border-b-0">
                    <div style={{ height: 300, width: '100%' }}>
                      {supportedBands.length > 0 && ulChartConfig && ulChartAdditionalParams && (
                        <DasPowerDetectorChart
                          config={ulChartConfig}
                          onFullscreenToggle={() => handleOpenFullscreen('ul')}
                          isFullscreen={false}
                          additionalParams={ulChartAdditionalParams}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={bands.length + 1} className="p-3 pt-0 align-top">
                    <Card>
                      <CardContent className="p-0">
                        <div className="flex divide-x divide-border">
                          {supportedBands.map((band) => (
                            <div key={band.number} className="flex-1 px-3 py-2">
                              <p className="text-xs text-muted-foreground mb-1">{band.name}</p>
                              <div className="flex items-center gap-1.5">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(band.ulPowerDetector)) }}
                                />
                                <span className="text-sm font-semibold text-foreground">{formatPower(band.ulPowerDetector)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
        </CardContent>
      )}
      </Card>

      {/* Global Alarms Section */}
      {hasGlobalAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={mruAlarmsGlob}
              alarmsMask={mruAlarmsMask}
              alarmDefinitions={globalAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {/* Band Alarms Section */}
      {hasBandAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Band Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Alarm</TableHead>
                  {bandAlarms.map((band) => (
                    <TableHead key={band.bandNumber} className="text-center min-w-[120px]">
                      {band.bandName}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {bandAlarmDefinitions.map((alarmDef) => (
                  <TableRow key={alarmDef.bit}>
                    <TableCell>{alarmDef.name}</TableCell>
                    {bandAlarms.map((band) => {
                      const isAlarmActive = isBitSet(band.alarms, alarmDef.bit);
                      const isMaskEnabled = isBitSet(band.mask, alarmDef.bit);
                      return (
                        <TableCell key={`${band.bandNumber}-${alarmDef.bit}`} className="text-center">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                              }}
                            />
                            <Checkbox
                              checked={isMaskEnabled}
                              disabled
                              size="small"
                              sx={{
                                padding: 0,
                                color: "var(--foreground)",
                                "&.Mui-disabled": { color: "var(--muted-foreground)" },
                              }}
                            />
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* DL Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'dl'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            DL Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'dl' && supportedBands.length > 0 && dlChartConfig && dlChartAdditionalParams && (
            <DasPowerDetectorChart
              config={dlChartConfig}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={dlChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* UL Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'ul'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            UL Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'ul' && supportedBands.length > 0 && ulChartConfig && ulChartAdditionalParams && (
            <DasPowerDetectorChart
              config={ulChartConfig}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={ulChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MruDeviceDetails;
